import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import EditIcon from "@material-ui/icons/Edit";
import { useIntl } from "react-intl";
import { DatePicker } from "../ui/DatePicker";
import { form } from "../messages";
import { useBmapi } from "../utils/bmapi-context";

function toYMD(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  const t = d.getTime();
  if (!Number.isFinite(t)) return "";
  return d.toISOString().substring(0, 10);
}

function parseToDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isFinite(d.getTime()) ? d : null;
}

function errorToText(e) {
  if (!e) return "Errore sconosciuto";
  if (typeof e === "string") return e;

  const msg =
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.response?.data ||
    e?.message;

  if (typeof msg === "string") return msg;

  try {
    return JSON.stringify(e, null, 2);
  } catch {
    return String(e);
  }
}

export default function UserInspectionProductDetail({
  item,
  onShowTransactions,
  updateExpiryDate = false,
  onExpiryDateUpdated,
  canReverseTransaction = false, // pass-through / futuro uso
}) {
  const { bmapi, startLoading, stopLoading } = useBmapi();
  const intl = useIntl();

  const [open, setOpen] = useState(false);
  const [initialDate, setInitialDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  // errore update scadenza
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorText, setErrorText] = useState("");

  // Hooks must not depend on item existence
  const initialYMD = useMemo(() => toYMD(initialDate), [initialDate]);
  const selectedYMD = useMemo(() => toYMD(selectedDate), [selectedDate]);
  const canConfirm = !!selectedYMD && selectedYMD !== initialYMD;

  // Keep dialog dates in sync when item changes while dialog open
  useEffect(() => {
    if (!open) return;
    const d = parseToDate(item?.expiration_date);
    setInitialDate(d);
    setSelectedDate(d);
  }, [open, item?.id, item?.expiration_date]);

  const handleOpen = () => {
    const d = parseToDate(item?.expiration_date);
    setInitialDate(d);
    setSelectedDate(d);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const openError = useCallback((e) => {
    setErrorText(errorToText(e));
    setErrorOpen(true);
  }, []);

  const closeError = () => {
    setErrorOpen(false);
    setErrorText("");
  };

  const handleConfirm = () => {
    if (!item?.id || !canConfirm) return;

    startLoading();
    return bmapi
      .updateProductExpiryDate(item.id, selectedYMD)
      .then(() => {
        setOpen(false);
        onExpiryDateUpdated?.();
      })
      .catch((e) => {
        openError(e);
      })
      .finally(stopLoading);
  };

  // Early return AFTER hooks
  if (!item) return null;

  return (
    <div style={{ width: "100%" }}>
      <table style={{ margin: "10px", width: "calc(100% - 20px)" }}>
        <tbody>
          <tr>
            <td>
              <Typography variant="h6" style={{ fontSize: "18px" }}>
                Id:
              </Typography>
              <Typography style={{ fontSize: "15px" }}>{item.id}</Typography>
            </td>
            <td>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Typography variant="h6" style={{ fontSize: "18px" }}>
                  Mostra transazioni:
                </Typography>
                <Typography style={{ fontSize: "18px", marginLeft: "6px" }}>
                  <InfoIcon
                    color="primary"
                    onClick={() => onShowTransactions?.(item.id)}
                    style={{ cursor: "pointer" }}
                    titleAccess={
                      canReverseTransaction
                        ? "Apri transazioni (storno abilitato)"
                        : "Apri transazioni"
                    }
                  />
                </Typography>
              </div>
            </td>
          </tr>

          <tr>
            <td>
              <Typography variant="h6" style={{ fontSize: "18px" }}>
                Campagna:
              </Typography>
              <Typography style={{ fontSize: "18px" }}>
                {item.campaign_name}
              </Typography>
            </td>
            <td>
              <Typography variant="h6" style={{ fontSize: "18px" }}>
                Business:
              </Typography>
              <Typography style={{ fontSize: "18px" }}>
                {item.business_name}
              </Typography>
            </td>
          </tr>

          <tr>
            <td>
              <Typography variant="h6" style={{ fontSize: "18px" }}>
                Tipo:
              </Typography>
              <Typography style={{ fontSize: "18px" }}>{item.type}</Typography>
            </td>
            <td>
              <Typography variant="h6" style={{ fontSize: "18px" }}>
                Sottotipo:
              </Typography>
              <Typography style={{ fontSize: "18px" }}>
                {item.subtype}
              </Typography>
            </td>
          </tr>

          <tr>
            <td>
              <Typography variant="h6" style={{ fontSize: "18px" }}>
                Creato:
              </Typography>
              <Typography style={{ fontSize: "18px" }}>
                {item.created_at}
              </Typography>
            </td>
            <td>
              <Typography variant="h6" style={{ fontSize: "18px" }}>
                Scadenza:
              </Typography>

              {updateExpiryDate ? (
                <Box
                  display="flex"
                  alignItems="center"
                  style={{ cursor: "pointer" }}
                  onClick={handleOpen}
                >
                  <Typography style={{ fontSize: "18px", marginRight: 6 }}>
                    {item.expiration_date}
                  </Typography>
                  <EditIcon fontSize="small" color="action" />
                </Box>
              ) : (
                <Typography style={{ fontSize: "18px" }}>
                  {item.expiration_date}
                </Typography>
              )}
            </td>
          </tr>

          <tr>
            <td>
              <Typography variant="h6" style={{ fontSize: "18px" }}>
                Aggiornato:
              </Typography>
              <Typography style={{ fontSize: "18px" }}>
                {item.updated_at}
              </Typography>
            </td>
            <td />
          </tr>

          <tr>
            <td>
              <Typography variant="h6" style={{ fontSize: "18px" }}>
                Stato:
              </Typography>
              <Typography style={{ fontSize: "18px" }}>
                {item.status}
              </Typography>
            </td>
            <td>
              <Typography variant="h6" style={{ fontSize: "18px" }}>
                Utilizzi disponibili:
              </Typography>
              <Typography style={{ fontSize: "18px" }}>
                {item.available_shots}
              </Typography>
            </td>
          </tr>

          <tr>
            <td>
              <Typography variant="h6" style={{ fontSize: "18px" }}>
                Valore:
              </Typography>
              <Typography style={{ fontSize: "18px" }}>{item.value}</Typography>
            </td>
            <td>
              <Typography variant="h6" style={{ fontSize: "18px" }}>
                Valore residuo:
              </Typography>
              <Typography style={{ fontSize: "18px" }}>
                {item.available_value}
              </Typography>
            </td>
          </tr>

          {item.play_date ||
          item.play_result ||
          item.play_prize ||
          item.batch_id ? (
            <>
              <tr>
                <td>
                  <Typography variant="h6" style={{ fontSize: "18px" }}>
                    Giorno giocata:
                  </Typography>
                  <Typography style={{ fontSize: "18px" }}>
                    {item.play_date}
                  </Typography>
                </td>
                <td>
                  <Typography variant="h6" style={{ fontSize: "18px" }}>
                    Esito giocata:
                  </Typography>
                  <Typography style={{ fontSize: "18px" }}>
                    {item.play_result}
                  </Typography>
                </td>
              </tr>

              <tr>
                <td>
                  <Typography variant="h6" style={{ fontSize: "18px" }}>
                    Premio giocata:
                  </Typography>
                  <Typography style={{ fontSize: "18px" }}>
                    {item.play_prize}
                  </Typography>
                </td>
                <td>
                  <Typography variant="h6" style={{ fontSize: "18px" }}>
                    Lotto:
                  </Typography>
                  <Typography style={{ fontSize: "18px" }}>
                    {item.batch_id}
                  </Typography>
                </td>
              </tr>
            </>
          ) : null}
        </tbody>
      </table>

      {/* Dialog update expiry */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Imposta la data di scadenza</DialogTitle>
        <DialogContent>
          <DatePicker
            label={intl.formatMessage(form.selectDate)}
            value={selectedDate}
            onChange={(d) => setSelectedDate(d)}
            fullWidth
            margin="normal"
            required
            clearable
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annulla</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog errore update expiry */}
      <Dialog open={errorOpen} onClose={closeError} fullWidth maxWidth="sm">
        <DialogTitle>Errore</DialogTitle>
        <DialogContent>
          <Typography style={{ whiteSpace: "pre-wrap" }}>
            {"Impossibile impostare la data di scadenza\n" + errorText}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeError} color="primary">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
